import React from 'react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import Questionnaire from '../questionnaire/questionnaire';
import Seasons from '../seasons/seasons';
import Climate from '../climate/climate';
import HeavyRain from '../heavyRain/heavyRain';
import Rescore from '../rescore/rescore';
import MspPrices from '../marketReference/mspPrices';
import CropNames from '../marketReference/cropNames';
import ReferenceCoverage from '../marketReference/referenceCoverage';
import { managementPageSubtitleSx, managementPageTitleSx } from '../../../styles/managementStyles';
import { EK_SCORE_TABS, ekScoreTabPath } from './ekScoreConfiguration.paths';

const SCREENS = {
    questionnaire: Questionnaire,
    seasons: Seasons,
    climate: Climate,
    heavyRain: HeavyRain,
    msp: MspPrices,
    cropNames: CropNames,
    coverage: ReferenceCoverage,
    rescore: Rescore,
};

// Segmented tab bar, the eKutir-green take on EAG FF BIO's Manage Partnership Farming `.seg-bar`.
const segBarSx = {
    display: 'flex',
    height: 48,
    mb: 4,
    overflowX: 'auto',
    overflowY: 'hidden',
    scrollbarWidth: 'none',
    '&::-webkit-scrollbar': { display: 'none' },
};

const segButtonSx = (active) => ({
    flex: 1,
    minWidth: 'max-content',
    height: 48,
    px: 1.5,
    py: 1.25,
    gap: 1,
    border: '1px solid',
    borderColor: active ? '#4F8506' : '#79747E',
    bgcolor: active ? '#EEF7E6' : 'transparent',
    color: active ? '#4F8506' : '#1C1B1F',
    zIndex: active ? 1 : 0,
    fontFamily: 'Roboto, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '20px',
    letterSpacing: '0.1px',
    whiteSpace: 'nowrap',
    transition: 'background-color 180ms ease, color 180ms ease',
    '&:first-of-type': { borderRadius: '100px 0 0 100px' },
    '&:last-of-type': { borderRadius: '0 100px 100px 0' },
    // Collapse the double border between neighbouring segments.
    '&:not(:first-of-type)': { ml: '-1px' },
    '&:hover': { bgcolor: active ? '#EEF7E6' : '#F1F1F1' },
});

export default function EkScoreConfiguration() {
    const navigate = useNavigate();
    const { tab } = useParams();
    const Screen = SCREENS[tab];

    if (!Screen) {
        return <Navigate to={ekScoreTabPath(EK_SCORE_TABS[0].key)} replace />;
    }

    return (
        <>
            <Box sx={{ mb: 3 }}>
                <Typography className="app-page-title" sx={managementPageTitleSx}>
                    eK Score Configuration
                </Typography>
                <Typography sx={managementPageSubtitleSx}>
                    Everything the eK Score engine is configured with — the questionnaire, the cropping calendar, the
                    climate baselines, the heavy-rain thresholds, MSP prices and crop names — a live check of what
                    is still missing, and re-scoring existing farmers.
                </Typography>
            </Box>

            <Box role="tablist" aria-label="eK Score configuration" sx={segBarSx}>
                {EK_SCORE_TABS.map((item) => {
                    const active = item.key === tab;
                    return (
                        <ButtonBase
                            key={item.key}
                            role="tab"
                            aria-selected={active}
                            data-testid={`ek-score-tab-${item.key}`}
                            onClick={active ? undefined : () => navigate(ekScoreTabPath(item.key))}
                            sx={segButtonSx(active)}
                        >
                            {item.label}
                        </ButtonBase>
                    );
                })}
            </Box>

            <Screen />
        </>
    );
}
